import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Eye, InfoIcon, Layers } from 'lucide-react';
import {
  TableCell,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { IPostDetailResponse } from '@/redux/post/post.d';
import { filterAttachmentsForCarousel } from '@/redux/post/post.utils';
import { AttachmentType } from '@/redux/attachment/attachment.d';

function ToolsDataCard({
  post,
  modelIndex,
  setModelIndex,
}: {
  post: IPostDetailResponse;
  modelIndex: number;
  setModelIndex: Dispatch<SetStateAction<number>>;
}) {
  const { attachments, toolData } = post;
  const [showRelatedTools, setShowRelatedTools] = useState<boolean>(false);
  const [filteredToolData, setFilteredToolData] = useState(post?.toolData);

  useEffect(() => {
    if (!post) return;
    const currentAttachment =
      filterAttachmentsForCarousel(attachments)[modelIndex];
    if (currentAttachment.resourceType !== AttachmentType.model) {
      setShowRelatedTools(false);
      setFilteredToolData(toolData);
    }
    const filteredData = showRelatedTools
      ? toolData.filter((tool) => tool.modelId === currentAttachment._id)
      : toolData;
    setFilteredToolData(filteredData);
  }, [modelIndex, toolData, attachments, showRelatedTools, post]);

  const handleCheckboxChange = () => {
    setShowRelatedTools(!showRelatedTools);
  };

  if (!toolData.length) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Card className="my-5">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex gap-2 items-center">
          <Layers className="h-5 w-5" />
          <CardTitle>Tools Data</CardTitle>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground flex gap-2 items-center">
            <InfoIcon size="18px" />
            Filter based on selected model
          </span>
          <Switch
            checked={showRelatedTools}
            onCheckedChange={handleCheckboxChange}
            disabled={
              filterAttachmentsForCarousel(attachments)[modelIndex]
                .resourceType !== AttachmentType.model
            }
          />
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Filename</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Tool Type</TableHead>
              <TableHead>Bit Type</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.isArray(filteredToolData) &&
              filteredToolData.map((tool, index) => (
                <TableRow key={tool._id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    {
                      filterAttachmentsForCarousel(attachments).find(
                        (attachment) => attachment._id === tool.modelId
                      )?.fileName
                    }
                  </TableCell>
                  {tool?.toolUsed ? (
                    <TableCell>
                      {tool.toolUsed} ({tool.toolUnit})
                    </TableCell>
                  ) : (
                    <TableCell>Nill</TableCell>
                  )}
                  <TableCell>
                    <a
                      href="https://www.onefinitycnc.com/shop/category/cnc-endmills"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      {tool?.laserOrEndmill || 'Nill'}
                    </a>
                  </TableCell>
                  <TableCell>
                    <a
                      href="https://www.onefinitycnc.com/shop/category/cnc-endmills"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      {tool?.toolType || 'Nill'}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const currentModelIndex = filterAttachmentsForCarousel(
                          attachments
                        ).findIndex(
                          (attachment) => attachment._id === tool.modelId
                        );
                        setModelIndex(currentModelIndex);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default ToolsDataCard;
